import React, { useState, useRef, useCallback, useEffect } from 'react';
import authService from '../../utils/authService';
import config from '../../config';

const HotelFilters = ({ filters, onFilterChange, onClearFilters, searchId }) => {
  const [localHotelName, setLocalHotelName] = useState(filters.hotelName || '');
  const [hotelSuggestions, setHotelSuggestions] = useState([]);
  const [showHotelSuggestions, setShowHotelSuggestions] = useState(false);
  const [hotelSuggestionsLoading, setHotelSuggestionsLoading] = useState(false);
  const [filterApplying, setFilterApplying] = useState(false);
  const hotelInputRef = useRef(null);
  const hotelDebounceTimerRef = useRef(null);
  const filterDebounceTimerRef = useRef(null);

  // Sync localHotelName when parent filters reset or change externally
  useEffect(() => {
    setLocalHotelName(filters.hotelName || '');
    if (!filters.hotelName) {
      if (hotelDebounceTimerRef.current) {
        clearTimeout(hotelDebounceTimerRef.current);
      }
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
      setShowHotelSuggestions(false);
      setHotelSuggestions([]);
    }
  }, [filters.hotelName]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hotelDebounceTimerRef.current) {
        clearTimeout(hotelDebounceTimerRef.current);
      }
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
    };
  }, []);

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (showHotelSuggestions && hotelInputRef.current) {
        const dropdown = document.querySelector('.hotel-suggestions');
        if (dropdown) {
          const rect = hotelInputRef.current.getBoundingClientRect();
          const newTop = rect.bottom + 8;
          const newLeft = rect.left;
          const newWidth = Math.max(rect.width, 250);
          
          dropdown.style.top = `${newTop}px`;
          dropdown.style.left = `${newLeft}px`;
          dropdown.style.width = `${newWidth}px`;
        }
      }
    };

    if (showHotelSuggestions) {
      // Update position immediately
      updateDropdownPosition();
      
      // Add event listeners for continuous updates
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      document.addEventListener('scroll', updateDropdownPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
        document.removeEventListener('scroll', updateDropdownPosition, { capture: true });
      };
    }
  }, [showHotelSuggestions]);

  const fetchHotelSuggestions = useCallback(async (query) => {
    if (!query.trim() || !searchId) {
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
      setHotelSuggestionsLoading(false);
      return;
    }

    try {
      setHotelSuggestionsLoading(true);
      
      const response = await fetch(
        `${config.API_BASE_URL}/api/search/hotel-suggestions?searchId=${encodeURIComponent(searchId)}&hotelName=${encodeURIComponent(query)}&page=0&size=10`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHotelSuggestions(data || []);
        setShowHotelSuggestions(data.length > 0);
      } else {
        setHotelSuggestions([]);
        setShowHotelSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching hotel suggestions:', error);
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
    } finally {
      setHotelSuggestionsLoading(false);
    }
  }, [searchId]);

  const handleHotelNameChange = (e) => {
    const value = e.target.value;
    setLocalHotelName(value); // Immediate local UI update - zero input delay
    
    // Clear existing timers
    if (hotelDebounceTimerRef.current) {
      clearTimeout(hotelDebounceTimerRef.current);
    }
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }
    
    // Debounce hotel suggestions (200ms)
    hotelDebounceTimerRef.current = setTimeout(() => {
      fetchHotelSuggestions(value);
    }, 200);
    
    // Debounce filter API call (400ms after user finishes typing)
    if (value.trim()) {
      filterDebounceTimerRef.current = setTimeout(() => {
        setFilterApplying(true);
        onFilterChange('hotelName', value);
        setTimeout(() => setFilterApplying(false), 200);
      }, 400);
    } else {
      setFilterApplying(true);
      onFilterChange('hotelName', '');
      setTimeout(() => setFilterApplying(false), 200);
    }
  };

  const handleHotelBlur = () => {
    setTimeout(() => {
      if (localHotelName && localHotelName.trim() && !showHotelSuggestions) {
        setFilterApplying(true);
        onFilterChange('hotelName', localHotelName);
        setTimeout(() => setFilterApplying(false), 200);
      }
      setShowHotelSuggestions(false);
    }, 150);
  };

  // Handle other filter changes with immediate effect
  const handleFilterChange = (filterName, value) => {
    console.log(`🔧 Filter change in HotelFilters: ${filterName} = ${value}`);
    setFilterApplying(true);
    onFilterChange(filterName, value);
    
    // Reset applying state after a short delay
    setTimeout(() => {
      setFilterApplying(false);
    }, 500);
  };

  const selectHotel = (hotelName) => {
    setLocalHotelName(hotelName);
    
    // Clear any existing timers first
    if (hotelDebounceTimerRef.current) {
      clearTimeout(hotelDebounceTimerRef.current);
    }
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }
    
    // Close suggestions immediately
    setShowHotelSuggestions(false);
    setHotelSuggestions([]);
    
    // Update the filter and trigger immediate application
    setFilterApplying(true);
    onFilterChange('hotelName', hotelName);
    
    setTimeout(() => {
      setFilterApplying(false);
    }, 200);
  };

  const handleHotelKeyDown = (e) => {
    if (!showHotelSuggestions || hotelSuggestions.length === 0) {
      // If Enter is pressed and no suggestions, apply the current filter
      if (e.key === 'Enter') {
        e.preventDefault();
        console.log('🔍 Applying hotel name filter via Enter key:', localHotelName);
        handleFilterChange('hotelName', localHotelName);
        setShowHotelSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const firstSuggestion = document.querySelector('.hotel-suggestions div:first-child');
        if (firstSuggestion) firstSuggestion.focus();
        break;
      case 'Escape':
        setShowHotelSuggestions(false);
        break;
      case 'Enter':
        e.preventDefault();
        if (hotelSuggestions.length > 0) {
          selectHotel(hotelSuggestions[0]);
        } else {
          // Apply current filter if no suggestions
          console.log('🔍 Applying hotel name filter via Enter key:', localHotelName);
          handleFilterChange('hotelName', localHotelName);
          setShowHotelSuggestions(false);
        }
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Filter Status */}
      <div className="flex items-center justify-between">
        {filterApplying && (
          <div className="flex items-center text-sm text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Applying filters...
          </div>
        )}
      </div>
      
      {/* Hotel Name Filter */}
      <div className="relative" style={{ zIndex: 1000 }}>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Hotel Name
        </label>
        <div className="relative">
          <input
            ref={hotelInputRef}
            type="text"
            value={localHotelName}
            onChange={handleHotelNameChange}
            onKeyDown={handleHotelKeyDown}
            onBlur={handleHotelBlur}
            placeholder="Search by hotel name..."
            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        {(showHotelSuggestions || hotelSuggestionsLoading) && (
          <div className="hotel-suggestions fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
               style={{ 
                 zIndex: 9999,
                 top: hotelInputRef.current ? hotelInputRef.current.getBoundingClientRect().bottom + 8 : 'auto',
                 left: hotelInputRef.current ? hotelInputRef.current.getBoundingClientRect().left : 'auto',
                 width: hotelInputRef.current ? Math.max(hotelInputRef.current.getBoundingClientRect().width, 250) : '250px',
                 minWidth: '250px'
               }}>
            {hotelSuggestionsLoading ? (
              <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading suggestions...
              </div>
            ) : hotelSuggestions.length > 0 ? (
              hotelSuggestions.map((hotel, index) => (
                <div
                  key={index}
                  tabIndex={0}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectHotel(hotel);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectHotel(hotel);
                  }}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm transition-colors duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {hotel}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                No suggestions found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Price Range
        </label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Minimum</label>
            <input
              type="number"
              min={filters.minPrice ?? 1000}
              max={filters.maxPrice ?? 200000}
              step="100"
              value={filters.minPrice ?? 1000}
              onChange={(e) => handleFilterChange('minPrice', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200"
              placeholder="Min"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Maximum</label>
            <input
              type="number"
              min={filters.minPrice ?? 1000}
              max={filters.maxPrice ?? 200000}
              step="100"
              value={filters.maxPrice ?? 200000}
              onChange={(e) => handleFilterChange('maxPrice', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200"
              placeholder="Max"
            />
          </div>
        </div>
        <div className="text-sm text-gray-600 text-center bg-gradient-to-r from-green-50 to-blue-50 rounded-lg py-2 px-3 border border-green-200">
          ₹{(filters.minPrice ?? 1000).toLocaleString()} - ₹{(filters.maxPrice ?? 200000).toLocaleString()}
        </div>
      </div>

      {/* Star Rating */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          Star Rating
        </label>
        <div className="space-y-3">
          {[
            { key: 'twoStars', label: '2 Stars', stars: 2, color: 'text-yellow-400' },
            { key: 'threeStars', label: '3 Stars', stars: 3, color: 'text-yellow-400' },
            { key: 'fourStars', label: '4 Stars', stars: 4, color: 'text-yellow-500' },
            { key: 'fiveStars', label: '5 Stars', stars: 5, color: 'text-yellow-500' }
          ].map(({ key, label, stars, color }) => (
            <label key={key} className="flex items-center cursor-pointer group p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <div className="relative">
                <input 
                  type="checkbox" 
                  checked={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 border-2 rounded-md transition-all duration-200 ${
                  filters[key] 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'border-gray-300 group-hover:border-blue-400'
                }`}>
                  {filters[key] && (
                    <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="ml-3 flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">{label}</span>
                <div className="flex">
                  {Array.from({ length: stars }, (_, i) => (
                    <svg key={i} className={`w-4 h-4 ${color}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  {Array.from({ length: 5 - stars }, (_, i) => (
                    <svg key={i} className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Matched Hotels Filter */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Multi-Vendor Rates
        </label>
        <label className="flex items-center cursor-pointer group p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
          <div className="relative">
            <input 
              type="checkbox" 
              checked={filters.onlyMatched || false}
              onChange={(e) => handleFilterChange('onlyMatched', e.target.checked)}
              className="sr-only"
            />
            <div className={`w-5 h-5 border-2 rounded-md transition-all duration-200 ${
              filters.onlyMatched 
                ? 'bg-blue-500 border-blue-500' 
                : 'border-gray-300 group-hover:border-blue-400'
            }`}>
              {filters.onlyMatched && (
                <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <div className="ml-3 flex flex-col">
            <span className="text-sm text-gray-900 font-medium flex items-center">
              Only Matched Hotels
              <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded">
                2+ Providers
              </span>
            </span>
            <span className="text-xs text-gray-500">Show hotels mapped across multiple suppliers</span>
          </div>
        </label>
      </div>

      {/* Clear Filters Button */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={onClearFilters}
          disabled={!onClearFilters}
          className="w-full bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default HotelFilters;